import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, toValue } from 'vue';
import type { MaybeRefOrGetter } from 'vue';

import type {
  CreateInternalContactRequest,
  InternalContact,
  InternalContactAssignment,
  PaginatedResponseInternalContact,
  UpdateInternalContactRequest,
} from '@stuut/api-sdk/models';

import { sdk } from '@/sdk';

const INTERNAL_CONTACTS_QUERY_KEY = {
  all: ['internal-contacts'] as const,
  lists: ['internal-contacts', 'list'] as const,
  list: (params: { limit: number; page: number }) => ['internal-contacts', 'list', params] as const,
  partner: (partnerId: string) => ['internal-contacts', 'partner', partnerId] as const,
};

type InternalContactsListData = PaginatedResponseInternalContact;

/**
 * A v2 internal contact normalized for the recipient pickers. Name fields are
 * `string | undefined` (not `null`) so the shape is assignable to UserAvatar's
 * `AvatarUser` prop.
 */
export interface InternalContactOption {
  email: string;
  familyName?: string;
  givenName?: string;
  name: string;
  phone: string | null;
}

/**
 * Filters partner internal-contact assignments to the ones relevant to the given
 * partner — those assigned to it plus org-wide/floating ones (`partnerId === null`) —
 * and normalizes them for the recipient pickers. Deduped by email.
 */
export function mapPartnerInternalContacts(
  assignments: InternalContactAssignment[],
  partnerId: string | undefined,
): InternalContactOption[] {
  const seen = new Set<string>();
  const options: InternalContactOption[] = [];

  for (const assignment of assignments) {
    if (assignment.partnerId !== null && assignment.partnerId !== partnerId) continue;

    const contact = assignment.internalContact;
    if (!contact.email) continue;

    const key = contact.email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    options.push({
      email: contact.email,
      name:
        contact.name ||
        [contact.givenName, contact.familyName].filter(Boolean).join(' ') ||
        contact.email,
      givenName: contact.givenName ?? undefined,
      familyName: contact.familyName ?? undefined,
      phone: contact.phone,
    });
  }

  return options;
}

/**
 * Normalizes a flat list of v2 internal contacts (e.g. from `GET /v2/internal-contacts`)
 * for the recipient pickers. Deduped by email.
 */
export function mapInternalContacts(contacts: InternalContact[]): InternalContactOption[] {
  const seen = new Set<string>();
  const options: InternalContactOption[] = [];

  for (const contact of contacts) {
    if (!contact.email) continue;

    const key = contact.email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    options.push({
      email: contact.email,
      name:
        contact.name ||
        [contact.givenName, contact.familyName].filter(Boolean).join(' ') ||
        contact.email,
      givenName: contact.givenName ?? undefined,
      familyName: contact.familyName ?? undefined,
      phone: contact.phone,
    });
  }

  return options;
}

export function useInternalContactsList(
  options: {
    enabled?: MaybeRefOrGetter<boolean>;
    limit?: MaybeRefOrGetter<number>;
    page?: MaybeRefOrGetter<number>;
  } = {},
) {
  const page = computed(() => toValue(options.page) ?? 0);
  const limit = computed(() => toValue(options.limit) ?? 100);

  return useQuery({
    enabled: options?.enabled,
    queryKey: computed(() =>
      INTERNAL_CONTACTS_QUERY_KEY.list({ limit: limit.value, page: page.value }),
    ),
    queryFn: () => sdk.internalContacts.list({ limit: limit.value, page: page.value }),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetches the internal contacts assigned to a partner (plus org-wide/floating ones)
 * and exposes them normalized for the recipient pickers. Disabled until a partnerId
 * is present and the optional `enabled` gate is truthy.
 */
export function usePartnerInternalContacts(
  partnerId: MaybeRefOrGetter<string | undefined>,
  options: { enabled?: MaybeRefOrGetter<boolean> } = {},
) {
  const enabled = computed(() => !!toValue(partnerId) && (toValue(options.enabled) ?? true));

  const query = useQuery({
    enabled,
    queryKey: computed(() => INTERNAL_CONTACTS_QUERY_KEY.partner(toValue(partnerId)!)),
    queryFn: () => sdk.partners.listInternalContacts({ partnerId: toValue(partnerId)! }),
    staleTime: 5 * 60 * 1000,
  });

  const contactOptions = computed(() =>
    mapPartnerInternalContacts(query.data.value ?? [], toValue(partnerId)),
  );

  return { query, options: contactOptions };
}

export function useCreateInternalContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ body }: { body: CreateInternalContactRequest }) =>
      sdk.internalContacts.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INTERNAL_CONTACTS_QUERY_KEY.all });
    },
  });
}

export function useUpdateInternalContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, body }: { contactId: string; body: UpdateInternalContactRequest }) =>
      sdk.internalContacts.update({ internalContactId: contactId, body }),
    onSuccess: (updated) => {
      queryClient.setQueriesData<InternalContactsListData>(
        { queryKey: INTERNAL_CONTACTS_QUERY_KEY.lists },
        (old) => {
          if (!old) return old;

          return {
            ...old,
            data: patchInternalContact(old.data, updated),
          };
        },
      );
    },
  });
}

function patchInternalContact(contacts: InternalContact[], updated: InternalContact) {
  return contacts.map((contact) =>
    contact.id === updated.id ? { ...contact, ...updated } : contact,
  );
}
